import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { documentToHtml, htmlToDocument, isSafeUrl } from '@entities/marketing-push/lib';
import type { RichTextDocument } from '../../../types/marketingPushInterfaces';

/**
 * Редактор форматированного текста уведомления.
 *
 * Сделан на contentEditable с document.execCommand. Компромисс осознанный,
 * и его стоит понимать перед доработками.
 *
 * Плюс — нулевые зависимости, а набор возможностей ровно тот, что описан
 * в контракте: пять оформлений, три типа блоков. Лишнего маркетолог не
 * наберёт даже случайно.
 *
 * Минус — execCommand формально устаревший. Работает во всех актуальных
 * браузерах и вряд ли исчезнет, но развивать на нём тяжело: вложенные
 * списки, таблицы, совместное редактирование. Если такое понадобится,
 * правильный шаг — TipTap или ProseMirror. Переезд дешевле, чем кажется:
 * сериализация живёт отдельно в entities/marketing-push/lib/richtext.ts
 * и от редактора не зависит, менять придётся только этот файл.
 *
 * Ключевая деталь реализации: contentEditable неуправляемый по природе.
 * Переписывать innerHTML на каждое изменение нельзя — курсор будет прыгать
 * в начало после каждой буквы. Поэтому HTML пишется только когда значение
 * пришло извне и отличается от того, что уже в поле.
 */

interface Props {
  value: RichTextDocument;
  onChange: (document: RichTextDocument) => void;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
}

interface Tool {
  command: string;
  label: string;
  title: string;
  argument?: string;
  style?: CSSProperties;
}

const TOOLS: Tool[] = [
  { command: 'bold', label: 'Ж', title: 'Полужирный (Ctrl+B)', style: { fontWeight: 700 } },
  { command: 'italic', label: 'К', title: 'Курсив (Ctrl+I)', style: { fontStyle: 'italic' } },
  {
    command: 'underline',
    label: 'Ч',
    title: 'Подчёркнутый (Ctrl+U)',
    style: { textDecoration: 'underline' },
  },
  {
    command: 'strikeThrough',
    label: 'З',
    title: 'Зачёркнутый',
    style: { textDecoration: 'line-through' },
  },
  { command: 'formatBlock', argument: 'h2', label: 'H2', title: 'Подзаголовок' },
  { command: 'formatBlock', argument: 'h3', label: 'H3', title: 'Подзаголовок меньше' },
  { command: 'formatBlock', argument: 'p', label: '¶', title: 'Обычный абзац' },
  { command: 'insertUnorderedList', label: '•', title: 'Список' },
  { command: 'insertOrderedList', label: '1.', title: 'Нумерованный список' },
];

export function PushRichTextEditor({
  value,
  onChange,
  disabled = false,
  placeholder = 'Текст уведомления',
  minHeight = 180,
}: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [empty, setEmpty] = useState(true);

  /** HTML, который мы сами записали. Нужен, чтобы отличить изменение
   *  извне от эха собственного onChange. */
  const writtenHtml = useRef<string>('');

  useEffect(() => {
    const element = editorRef.current;
    if (!element) return;

    const html = documentToHtml(value);
    if (html === writtenHtml.current) return;

    element.innerHTML = html;
    writtenHtml.current = html;
    setEmpty((element.textContent ?? '').trim().length === 0);
  }, [value]);

  const emit = useCallback(() => {
    const element = editorRef.current;
    if (!element) return;

    writtenHtml.current = element.innerHTML;
    setEmpty((element.textContent ?? '').trim().length === 0);
    onChange(htmlToDocument(element));
  }, [onChange]);

  const exec = useCallback(
    (tool: Tool) => {
      const element = editorRef.current;
      if (!element || disabled) return;
      element.focus();
      document.execCommand(tool.command, false, tool.argument);
      emit();
    },
    [disabled, emit],
  );

  const insertLink = useCallback(() => {
    const element = editorRef.current;
    if (!element || disabled) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      window.alert('Выделите текст, который должен стать ссылкой.');
      return;
    }

    const url = window.prompt('Адрес ссылки', 'https://');
    if (url === null) return;

    if (!isSafeUrl(url)) {
      // Проверяем здесь, а не при сохранении: иначе маркетолог узнает
      // о проблеме через несколько экранов после того, как её создал.
      window.alert('Ссылка должна начинаться с http:// или https://');
      return;
    }

    element.focus();
    document.execCommand('createLink', false, url);
    emit();
  }, [disabled, emit]);

  const removeLink = useCallback(() => {
    if (!editorRef.current || disabled) return;
    editorRef.current.focus();
    document.execCommand('unlink');
    emit();
  }, [disabled, emit]);

  /**
   * Вставка только текстом.
   *
   * Без этого вставленное из Word или письма приносит десятки инлайновых
   * стилей и чужих тегов. Сериализатор их отбросит, но сначала они попадут
   * в поле — и маркетолог увидит одно, а сохранит другое. Худший вариант,
   * поэтому режем на входе.
   */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      const text = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);
      emit();
    },
    [emit],
  );

  return (
    <div
      className={`rounded-lg border bg-white transition-colors ${
        focused ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-300'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <div
        role="toolbar"
        aria-label="Форматирование"
        className="flex flex-wrap gap-1 rounded-t-lg border-b border-gray-200 bg-gray-50 p-2"
      >
        {TOOLS.map((tool) => (
          <ToolbarButton
            key={`${tool.command}-${tool.argument ?? ''}`}
            title={tool.title}
            disabled={disabled}
            style={tool.style}
            onClick={() => exec(tool)}
          >
            {tool.label}
          </ToolbarButton>
        ))}
        <span className="w-2" />
        <ToolbarButton title="Вставить ссылку" disabled={disabled} onClick={insertLink}>
          Ссылка
        </ToolbarButton>
        <ToolbarButton title="Убрать ссылку" disabled={disabled} onClick={removeLink}>
          Убрать
        </ToolbarButton>
      </div>

      <div className="relative">
        <div
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            emit();
          }}
          onPaste={handlePaste}
          className="mp-rich-text px-3 py-3 text-sm text-[#2d2c2c] outline-none"
          style={{ minHeight }}
        />
        {empty && (
          <span aria-hidden className="pointer-events-none absolute left-3 top-3 text-sm text-gray-400">
            {placeholder}
          </span>
        )}
      </div>
    </div>
  );
}

function ToolbarButton({
  children,
  title,
  onClick,
  disabled,
  style,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      // onMouseDown, а не onClick: клик по кнопке снимает выделение
      // в contentEditable раньше, чем сработает обработчик, и команда
      // применяется в пустоту.
      onMouseDown={(event) => {
        event.preventDefault();
        onClick();
      }}
      style={style}
      className="h-7 min-w-[28px] rounded border border-gray-300 bg-white px-2 text-sm text-[#2d2c2c] hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export default PushRichTextEditor;
